import { createAsyncThunk, createEntityAdapter, createSelector, createSlice, EntityId, EntityState, PayloadAction } from '@reduxjs/toolkit'
import { ZapierTemplate } from '@sendhub/integrations/api-client-typescript-axios'
import { RootState } from '../root'
import { flatMap } from 'lodash'
import axios from 'axios'
import { integrationsApi } from '../../services/integrations-api'

// TODO: find a better place for this utility function...
export function getMainAppSlugFromTemplate(template: ZapierTemplate | undefined): string | undefined {
  // Guard against a missing template / `steps` array (or steps missing a `url`).
  // `steps` is required in the spec, but two things violate it at runtime: the
  // Zapier API occasionally returns partial step data, and corrupted persisted
  // state can rehydrate `selectAll` into an array containing `undefined` (a stale
  // setMany(htmlString) leaves ids:[undefined] -> JSON null -> entities miss).
  // Either way, one bad entry must not crash the whole integrations list render.
  return (template?.steps ?? [])
    .map((step) => step.url?.match(/^https:\/\/zapier.com\/apps\/(.+)\//)?.[1])
    .find((maybeSlug) => maybeSlug && maybeSlug.toLowerCase() !== 'sendhub')

  // const split = template.url.split('/')
  // return [split[4], split[6]].find((app) => app !== 'sendhub')
}

// The /zap-templates proxy can respond with an HTML body (e.g. a 500/login error
// page) instead of the expected JSON array. axios hands that back as a string,
// and dispatching it into the entity adapter's `setMany` makes RTK iterate the
// string character-by-character — flooding the console with "selectId returned
// undefined" warnings and storing junk single-char "templates" that later crash
// getMainAppSlugFromTemplate. Coerce to a real array (logging unexpected shapes).
function asTemplateArray(data: unknown): ZapierTemplate[] {
  if (Array.isArray(data)) return data
  console.error('listZapierTemplates returned a non-array response; ignoring it', data)
  return []
}

export const ZAPIER_TEMPLATES_FEATURE_KEY = 'zapierTemplates'

export interface ZapierTemplatesState extends EntityState<Readonly<ZapierTemplate>> {
  loadingStatus: 'not loaded' | 'loading' | 'loaded' | 'error'
  error?: string | null
}

export const zapierTemplatesAdapter = createEntityAdapter<Readonly<ZapierTemplate>>()

/**
 * Export an effect using createAsyncThunk from
 * the Redux Toolkit: https://redux-toolkit.js.org/api/createAsyncThunk
 *
 * e.g.
 * ```
 * import React, { useEffect } from 'react';
 * import { useDispatch } from 'react-redux';
 *
 * // ...
 *
 * const dispatch = useDispatch();
 * useEffect(() => {
 *   dispatch(fetchZapierTemplates())
 * }, [dispatch]);
 * ```
 */
export const fetchZapierTemplates = createAsyncThunk('zapierTemplates/fetchStatus', async (_, thunkAPI) => {
  const PAGE_SIZE = 100
  // #TODO: we only need to specify these templateIds to bust cloudfront's cache... 2.5 days edge cache for an API is crazy
  const templateIds = [
    28674, 1176494, 1176501, 1177423, 1177528, 1177538, 1177552, 1177557, 1177584, 1177591, 1177605, 1177623, 1177638, 1178018, 8778, 8782,
  ]

  const cacheBusterPromise = integrationsApi
    .listZapierTemplates(templateIds.join(','), undefined, PAGE_SIZE, 0)
    .then((resp) => thunkAPI.dispatch(zapierTemplatesSlice.actions.setMany(asTemplateArray(resp.data))))

  async function pageAllZapierTemplates(
    callback: (data: ZapierTemplate[]) => void = (zapTemplates) => thunkAPI.dispatch(zapierTemplatesSlice.actions.setMany(zapTemplates)),
    limit: number = PAGE_SIZE,
    offset = 0,
    results: ZapierTemplate[] = []
  ) {
    const response = await integrationsApi.listZapierTemplates(undefined, undefined, limit, offset)
    const templates = asTemplateArray(response.data)

    results.push(...templates)
    callback(templates)

    if (templates.length === limit) {
      await pageAllZapierTemplates(callback, limit, offset + limit, results)
    }
  }

  // const allPagedZapierTemplatesPromise = zapierApi.listZapierTemplates(undefined, undefined, PAGE_SIZE, 0).then(async (firstResp) => {
  //   let templates: ZapierTemplate[] = firstResp.data
  //   // let more = firstResp.data.filter((t) => !templates.map((template) => template.id).includes(t.id)).length === PAGE_SIZE
  //   let more = firstResp.data.length === PAGE_SIZE
  //   let offset = 0
  //   while (more) {
  //     const resp = await zapierApi.listZapierTemplates(undefined, undefined, PAGE_SIZE, offset)
  //     thunkAPI.dispatch(zapierTemplatesSlice.actions.setMany(resp.data))
  //     templates = [...templates, ...resp.data]
  //     offset += PAGE_SIZE
  //     if (firstResp.data.length < PAGE_SIZE) {
  //       console.log(
  //         `firstResp.data.length: ${firstResp.data.length}\nPAGE_SIZE: ${PAGE_SIZE}\nfirstResp.data.length < PAGE_SIZE: ${
  //           firstResp.data.length < PAGE_SIZE
  //         }`
  //       )
  //       more = false
  //       break
  //     }
  //   }
  // })

  return Promise.all([cacheBusterPromise, pageAllZapierTemplates()])
})

export const initialZapierTemplatesState: ZapierTemplatesState = zapierTemplatesAdapter.getInitialState({
  loadingStatus: 'not loaded',
  error: null,
})

export const zapierTemplatesSlice = createSlice({
  name: ZAPIER_TEMPLATES_FEATURE_KEY,
  initialState: initialZapierTemplatesState,
  reducers: {
    add: zapierTemplatesAdapter.addOne,
    setMany: zapierTemplatesAdapter.setMany,
    remove: zapierTemplatesAdapter.removeOne,
    // ...
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchZapierTemplates.pending, (state: ZapierTemplatesState) => {
        state.loadingStatus = 'loading'
      })
      .addCase(fetchZapierTemplates.fulfilled, (state: ZapierTemplatesState, action) => {
        // zapierTemplatesAdapter.setAll(state, action.payload)
        state.loadingStatus = 'loaded'
      })
      .addCase(fetchZapierTemplates.rejected, (state: ZapierTemplatesState, action) => {
        state.loadingStatus = 'error'
        state.error = action.error.message
      })
  },
})

/*
 * Export reducer for store configuration.
 */
export const zapierTemplatesReducer = zapierTemplatesSlice.reducer

/*
 * Export action creators to be dispatched. For use with the `useDispatch` hook.
 *
 * e.g.
 * ```
 * import React, { useEffect } from 'react';
 * import { useDispatch } from 'react-redux';
 *
 * // ...
 *
 * const dispatch = useDispatch();
 * useEffect(() => {
 *   dispatch(zapierTemplatesActions.add({ id: 1 }))
 * }, [dispatch]);
 * ```
 *
 * See: https://react-redux.js.org/next/api/hooks#usedispatch
 */
export const zapierTemplatesActions = zapierTemplatesSlice.actions

/*
 * Export selectors to query state. For use with the `useSelector` hook.
 *
 * e.g.
 * ```
 * import { useSelector } from 'react-redux';
 *
 * // ...
 *
 * const entities = useSelector(selectAllZapierTemplates);
 * ```
 *
 * See: https://react-redux.js.org/next/api/hooks#useselector
 */
const { selectAll, selectEntities } = zapierTemplatesAdapter.getSelectors()

export const getZapierTemplatesState = (rootState: RootState): ZapierTemplatesState => rootState[ZAPIER_TEMPLATES_FEATURE_KEY]

export const selectAllZapierTemplates = createSelector(getZapierTemplatesState, selectAll)
export const selectZapierTemplatesByAppId = (zapierAppId: EntityId) =>
  createSelector(selectAllZapierTemplates, (zapierTemplates) =>
    zapierTemplates.filter((t) => zapierAppId === getMainAppSlugFromTemplate(t))
  )

export const selectZapierTemplatesEntities = createSelector(getZapierTemplatesState, selectEntities)
export const selectZapierTemplatesLoadingStatus = createSelector(getZapierTemplatesState, (state) => state.loadingStatus)
export const selectZapierTemplatesError = createSelector(getZapierTemplatesState, (state) => state.error)
